import Avatar, { IAvatar } from "../../Avatar";
import { userTypeConvert } from "@/utils/helper";
import Link from "next/link";
import { PAGE_URL } from "@/utils/constants";
import { motion } from "framer-motion";
import { capitalize } from "lodash";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import ModalMutualConnection from "../../pages/connection/ModalMutualConnection";
import useOrgSettingStore from "@/store/useOrgSettingsStore";
import { useGetCGPercents } from "@/hooks/useUsers";
import { useGetCGPercentContextWait } from "@/hooks/useCircles";
import CardUserProfileView from "./CardUserProfileView";

interface ICardUserProfile {
  styleTier?: CSSProperties;
  reference?: string;
  findCGCTX?: "EVENT" | "CIRCLE";
  cgPercent?: number | null;
  mutualCount?: number;
  hideJobType?: boolean;
  hideCG?: boolean;
  item: {
    id: string;
    email: string;
    organizationGroup: string;
    firstName: string;
    lastName: string;
    avatarUrl: string;
    jobTitle: string;
    type: string;
    lookingForType: string;
    points: number;
    department: {
      id: string;
      name: string;
    };
  };
  variant?: "vertical" | "horizontal";
  className?: string;
  hideType?: boolean;
  isChampion?: boolean;
  disableHref?: boolean;
  hideMutual?: boolean;
  points?: number;
  avatarSize?: "xxs" | "xs" | "sm" | "sm" | "md" | "lg" | "xl" | "2xl";
  customType?: string | any;
  squareAvatar?: boolean;
  findCG?: boolean;
  avatarOnly?: boolean;
  hideOnlyType?: boolean;
  containerClassName?: string;
  type?: "profile" | "circle" | "event";
  isOneLineName?: boolean;
  isNameCapitalized?: boolean;
  nameClassName?: string;
}

export const useTypeTier = (points: number) => {
  const { pointTiers } = useOrgSettingStore();

  const [result, resultSet] = useState("");

  useEffect(() => {
    if (points === null) {
      return;
    }

    if (pointTiers?.platinum != null && points >= pointTiers?.platinum)
      return resultSet("PLATINUM");
    if (
      pointTiers?.gold !== undefined &&
      pointTiers?.gold !== null &&
      points >= pointTiers?.gold
    )
      return resultSet("GOLD");
    if (pointTiers?.silver != null && points >= pointTiers?.silver)
      return resultSet("SILVER");
    if (pointTiers?.bronze != null && points >= pointTiers?.bronze)
      return resultSet("BRONZE");
  }, [points]);

  return result;
};

export const useTypeTierFunc = () => {
  const { pointTiers } = useOrgSettingStore();

  function typeTierConverter(points: number) {
    if (points === null) {
      return;
    }

    if (pointTiers?.platinum != null && points >= pointTiers?.platinum)
      return "PLATINUM";
    if (pointTiers?.gold != null && points >= pointTiers?.gold) return "GOLD";
    if (pointTiers?.silver != null && points >= pointTiers?.silver)
      return "SILVER";
    if (pointTiers?.bronze != null && points >= pointTiers?.bronze)
      return "BRONZE";
  }
  return { typeTierConverter };
};

export default function CardUserProfile({
  item,
  cgPercent = null,
  mutualCount,
  variant = "vertical",
  className,
  hideType,
  isChampion,
  disableHref,
  hideMutual,
  points,
  avatarSize,
  customType,
  squareAvatar,
  findCG,
  hideJobType,
  avatarOnly,
  findCGCTX,
  reference,
  styleTier,
  hideOnlyType,
  hideCG,
  containerClassName = "",
  type,
  isOneLineName = false,
  isNameCapitalized = true,
  nameClassName,
}: ICardUserProfile) {
  const [showMutualConnection, setShowMutualConnection] = useState<{
    isOpen: boolean;
    userId: any;
  }>({
    isOpen: false,
    userId: null,
  });

  const { data: cgGetSendiri, refetch } = useGetCGPercents(
    [item?.id],
    1,
    reference
  );
  const { data: cgGetSendiriContext, refetch: refetchCGPContext } =
    useGetCGPercentContextWait(
      {
        referenceIds: [item?.id],
        limit: 1,
        type: findCGCTX,
      },
      reference
    );

  useEffect(() => {
    if (findCG && item) {
      refetch();
    }
  }, [findCG, item, refetch]);

  useEffect(() => {
    if (findCGCTX && item) {
      refetchCGPContext();
    }
  }, [findCGCTX, item, refetchCGPContext]);

  const LinkMotion = motion(Link);

  const Wrapper = ({ children }: { children: any }) =>
    disableHref ? (
      <motion.div className={className}>{children}</motion.div>
    ) : (
      <LinkMotion
        href={`${PAGE_URL.profile}/${item?.id}`}
        whileHover={{ scale: 0.99 }}
        className={className}
      >
        {children}
      </LinkMotion>
    );

  const { pointTiers } = useOrgSettingStore();
  const typeTier = () => {
    if (
      pointTiers?.platinum &&
      item?.points &&
      item?.points >= pointTiers?.platinum
    )
      return "PLATINUM";
    if (pointTiers?.gold && item?.points && item?.points >= pointTiers?.gold)
      return "GOLD";
    if (
      pointTiers?.silver &&
      item?.points &&
      item?.points >= pointTiers?.silver
    )
      return "SILVER";
    if (
      pointTiers?.bronze != null &&
      item?.points != null &&
      item?.points >= pointTiers?.bronze
    )
      return "BRONZE";
    return "";
  };

  const CGP = useMemo(() => {
    if (cgPercent === null) {
      if (cgGetSendiri?.[0]?.CGPercent) {
        return cgGetSendiri[0]?.CGPercent;
      } else if (cgGetSendiriContext?.[0]?.CGPercent) {
        return cgGetSendiriContext[0]?.CGPercent;
      } else {
        return null;
      }
    } else {
      return cgPercent;
    }
  }, [cgGetSendiri, cgGetSendiriContext, cgPercent]);
  // console.log("🚀 ~ CGP:", CGP)

  return (
    <>
      <CardUserProfileView
        nameClassName={nameClassName}
        containerClassName={containerClassName}
        item={item}
        CGP={CGP}
        avatarOnly={avatarOnly}
        avatarSize={avatarSize}
        className={className}
        customType={customType}
        disableHref={disableHref}
        hideCG={hideCG}
        hideJobType={hideJobType}
        hideMutual={hideMutual}
        hideOnlyType={hideOnlyType}
        hideType={hideType}
        isChampion={isChampion}
        mutualCount={mutualCount}
        squareAvatar={squareAvatar}
        styleTier={styleTier}
        variant={variant}
        type={type}
        isOneLineName={isOneLineName}
        isNameCapitalized={isNameCapitalized}
      />
    </>
  );
}
